import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { KeyValue, Pill } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateLabel, num } from '../../lib/format';
import type { Customer } from '../../lib/types';

const EDIT_FIELDS = [
  'legal_name', 'trade_name', 'tax_id', 'email', 'phone',
  'address', 'city', 'province', 'postal_code', 'currency', 'payment_terms_days',
] as const;

function CustomerDetail({ row, onSaved }: { row: Customer; onSaved: () => void }) {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string | number>>({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const startEdit = () => {
    setDraft({
      legal_name: row.legal_name, trade_name: row.trade_name ?? '', tax_id: row.tax_id ?? '',
      email: row.email ?? '', phone: row.phone ?? '', address: row.address ?? '',
      city: row.city ?? '', province: row.province ?? '', postal_code: row.postal_code ?? '',
      currency: row.currency, payment_terms_days: row.payment_terms_days,
    });
    setErr(null); setOk(false); setEditing(true);
  };

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const body: Record<string, unknown> = {};
      for (const f of EDIT_FIELDS) {
        if (draft[f] !== undefined) body[f] = draft[f];
      }
      await api.patch<{ data: Customer }>(`/api/records/customers/${row.id}`, body);
      setEditing(false);
      setOk(true);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const set = (k: string, v: string | number) => setDraft((d) => ({ ...d, [k]: v }));
  const field = (k: string, label: string, type = 'text') => (
    <label className="field">
      <label>{label}</label>
      <input className="txt" type={type} value={String(draft[k] ?? '')} onChange={(e) => set(k, e.target.value)} />
    </label>
  );

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{row.code}</span>
        <h3>{row.legal_name}</h3>
        {row.trade_name && row.trade_name !== row.legal_name ? <span className="muted">· {row.trade_name}</span> : null}
        <Pill tone={row.active ? 'ok' : 'neutral'} label={row.active ? '启用' : '停用'} />
        <div className="spacer" />
        {!editing && isAdmin ? <button className="btn" onClick={startEdit}>编辑客户资料</button> : null}
      </div>

      {err ? <div className="notice bad">{err}</div> : null}
      {ok && !editing ? <div className="notice info">已保存，列表数据已刷新（写入真实核心服务）。</div> : null}

      {editing ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
          {field('legal_name', '法定名称')}
          {field('trade_name', '经营名称')}
          {field('tax_id', '税号 / CUIT')}
          {field('email', '邮箱', 'email')}
          {field('phone', '电话')}
          {field('city', '城市')}
          {field('province', '省份')}
          {field('postal_code', '邮编')}
          {field('address', '地址')}
          {field('currency', '币种')}
          {field('payment_terms_days', '账期（天）', 'number')}
          <div className="detail-actions" style={{ gridColumn: '1 / -1' }}>
            <button className="btn primary" onClick={save} disabled={saving}>{saving ? '保存中…' : '保存修改'}</button>
            <button className="btn" onClick={() => { setEditing(false); setErr(null); }} disabled={saving}>取消</button>
          </div>
        </div>
      ) : (
        <div className="kv-grid">
          <KeyValue k="客户编码">{row.code}</KeyValue>
          <KeyValue k="税号 / CUIT">{row.tax_id || '—'}</KeyValue>
          <KeyValue k="税类">{row.tax_category || '—'}</KeyValue>
          <KeyValue k="城市">{row.city || '—'}</KeyValue>
          <KeyValue k="省份">{row.province || '—'}</KeyValue>
          <KeyValue k="国家">{row.country || '—'}</KeyValue>
          <KeyValue k="邮箱">{row.email || '—'}</KeyValue>
          <KeyValue k="电话">{row.phone || '—'}</KeyValue>
          <KeyValue k="币种">{row.currency}</KeyValue>
          <KeyValue k="账期">{num(row.payment_terms_days, 0)} 天</KeyValue>
          <KeyValue k="地址" >{row.address || '—'}</KeyValue>
          <KeyValue k="创建时间">{dateLabel(row.created_at)}</KeyValue>
        </div>
      )}
    </div>
  );
}

export function CustomersPage() {
  const [reloadSignal, setReloadSignal] = useState(0);
  const [sp] = useSearchParams();

  const columns: Column<Customer>[] = [
    { header: '客户名称', render: (c) => <span className="strong">{c.legal_name}</span> },
    { header: '编码', render: (c) => <span className="num">{c.code}</span> },
    { header: '税号 / CUIT', render: (c) => <span className="num muted">{c.tax_id || '—'}</span> },
    { header: '城市', render: (c) => c.city || '—' },
    { header: '省份', render: (c) => c.province || '—' },
    { header: '币种', render: (c) => <span className="muted">{c.currency}</span> },
    { header: '账期(天)', align: 'right', render: (c) => <span className="num">{num(c.payment_terms_days, 0)}</span> },
    { header: '状态', render: (c) => <Pill tone={c.active ? 'ok' : 'neutral'} label={c.active ? '启用' : '停用'} /> },
  ];

  return (
    <ListPage<Customer>
      title="客户"
      desc="客户主数据 · 来自核心服务 /api/customers，支持关键字搜索与当页展开（管理员可就地编辑）"
      endpoint="/api/customers"
      columns={columns}
      rowId={(c) => c.id}
      showYear={false}
      searchPlaceholder="搜索编码 / 名称 / 税号 / 邮箱…"
      initialQuery={sp.get('q') || ''}
      reloadSignal={reloadSignal}
      rowActions={(row) => <RecordActions type="customers" row={row} fields={RECORD_FIELDS['customers']} label={String(row.legal_name)} onDone={() => setReloadSignal((n) => n + 1)} />}
      renderDetail={(row) => <CustomerDetail row={row} onSaved={() => setReloadSignal((n) => n + 1)} />}
    />
  );
}
